import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import Footer from '../components/Footer';
import { loadState } from '../utils/stateManager';
import { generatePackage } from '../utils/bicepGenerator';
import './Page9CodeViewer.css';

function Page9CodeViewer() {
  const navigate = useNavigate();
  const [files, setFiles] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [copiedFile, setCopiedFile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const state = loadState();

    if (state.generatedFiles && Object.keys(state.generatedFiles).length > 0) {
      setFiles(state.generatedFiles);
      setActiveTab(Object.keys(state.generatedFiles)[0]);
      setLoading(false);
    } else {
      fetchFiles(state);
    }
  }, []);

  const fetchFiles = (state) => {
    const data = generatePackage(state);
    setFiles(data.files);
    setActiveTab(Object.keys(data.files)[0]);
    setLoading(false);
  };

  const handleCopyFile = async (filename) => {
    try {
      await navigator.clipboard.writeText(files[filename]);
      setCopiedFile(filename);
      setTimeout(() => setCopiedFile(null), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const handleCopyAll = async () => {
    const allContent = Object.entries(files)
      .map(([filename, content]) => `// ===== ${filename} =====\n\n${content}`)
      .join('\n\n');

    try {
      await navigator.clipboard.writeText(allContent);
      setCopiedFile('__all__');
      setTimeout(() => setCopiedFile(null), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const handleDownload = () => {
    Object.entries(files).forEach(([filename, content]) => {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  const handleBack = () => {
    navigate('/page8');
  };

  if (loading) {
    return <div className="page-container"><p>Loading generated code...</p></div>;
  }

  if (!files) {
    return (
      <div className="page-container">
        <p>Unable to load generated files. Please go back and try again.</p>
        <button className="btn-secondary" onClick={handleBack}>← Back to Preview</button>
      </div>
    );
  }

  const fileNames = Object.keys(files);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Azure VM Deployment Wizard</h1>
        <ProgressBar currentPage={9} />
      </div>

      <div className="page-content">
        <div className="code-viewer-header">
          <h2>Generated Bicep Code</h2>
          <div className="code-viewer-actions">
            <button
              className="btn-copy-all"
              onClick={handleCopyAll}
            >
              {copiedFile === '__all__' ? 'Copied!' : 'Copy All Files'}
            </button>
            <button className="btn-download" onClick={handleDownload}>
              📥 Download All
            </button>
          </div>
        </div>

        <div className="code-tabs">
          {fileNames.map((filename) => (
            <button
              key={filename}
              className={`code-tab ${activeTab === filename ? 'active' : ''}`}
              onClick={() => setActiveTab(filename)}
            >
              {filename}
            </button>
          ))}
        </div>

        <div className="code-panel">
          <div className="code-panel-header">
            <span className="code-filename">{activeTab}</span>
            <button
              className="btn-copy"
              onClick={() => handleCopyFile(activeTab)}
            >
              {copiedFile === activeTab ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre className="code-block"><code>{files[activeTab]}</code></pre>
        </div>

        <div className="file-count">
          {fileNames.length} file{fileNames.length !== 1 ? 's' : ''} generated
        </div>
      </div>

      <div className="page-actions">
        <button className="btn-secondary" onClick={handleBack}>
          ← Back to Preview
        </button>
        <button className="btn-primary" onClick={handleDownload}>
          📥 Download Deployment Package
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Page9CodeViewer;
